/**
 * useFullScreen — Hook for toggling browser Fullscreen API.
 *
 * Provides isFullScreen state and a toggle function.
 * Falls back gracefully if the Fullscreen API is not supported.
 * Listens for Escape key and fullscreenchange events.
 */

import { useState, useCallback, useEffect, useRef } from 'react';

export interface UseFullScreenReturn {
  isFullScreen: boolean;
  toggleFullScreen: () => void;
  enterFullScreen: () => void;
  exitFullScreen: () => void;
  isSupported: boolean;
}

export function useFullScreen(elementRef?: React.RefObject<HTMLElement>): UseFullScreenReturn {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const internalRef = useRef<HTMLElement | null>(null);

  const isSupported =
    typeof document !== 'undefined' &&
    (!!document.fullscreenEnabled ||
      !!(document as any).webkitFullscreenEnabled ||
      !!(document as any).mozFullScreenEnabled ||
      !!(document as any).msFullscreenEnabled);

  // Sync state with actual fullscreen status
  useEffect(() => {
    const handleChange = () => {
      const fsElement =
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement;
      setIsFullScreen(!!fsElement);
    };

    document.addEventListener('fullscreenchange', handleChange);
    document.addEventListener('webkitfullscreenchange', handleChange);
    document.addEventListener('mozfullscreenchange', handleChange);
    document.addEventListener('MSFullscreenChange', handleChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleChange);
      document.removeEventListener('webkitfullscreenchange', handleChange);
      document.removeEventListener('mozfullscreenchange', handleChange);
      document.removeEventListener('MSFullscreenChange', handleChange);
    };
  }, []);

  const getElement = useCallback((): HTMLElement => {
    if (elementRef?.current) return elementRef.current;
    if (internalRef.current) return internalRef.current;
    return document.documentElement;
  }, [elementRef]);

  const enterFullScreen = useCallback(async () => {
    if (!isSupported) return;
    const el = getElement();
    try {
      if (el.requestFullscreen) {
        await el.requestFullscreen();
      } else if ((el as any).webkitRequestFullscreen) {
        await (el as any).webkitRequestFullscreen();
      } else if ((el as any).mozRequestFullScreen) {
        await (el as any).mozRequestFullScreen();
      } else if ((el as any).msRequestFullscreen) {
        await (el as any).msRequestFullscreen();
      }
    } catch {
      // Fullscreen request may fail if not triggered by user gesture
    }
  }, [isSupported, getElement]);

  const exitFullScreen = useCallback(async () => {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        await (document as any).webkitExitFullscreen();
      } else if ((document as any).mozCancelFullScreen) {
        await (document as any).mozCancelFullScreen();
      } else if ((document as any).msExitFullscreen) {
        await (document as any).msExitFullscreen();
      }
    } catch {
      // May fail if not in fullscreen
    }
  }, []);

  const toggleFullScreen = useCallback(() => {
    if (isFullScreen) {
      exitFullScreen();
    } else {
      enterFullScreen();
    }
  }, [isFullScreen, enterFullScreen, exitFullScreen]);

  return { isFullScreen, toggleFullScreen, enterFullScreen, exitFullScreen, isSupported };
}
